<template>
  <tr>
    <td id="job-pipeline-rating">
      <div class="d-flex align-items-center">
        <span class="mr-3 star-rating">
          <star-rating
            class="star-rating-vue"
            active-color="#f05c00"
            border-color="#DDD"
            :read-only="application.is_deleted"
            :animate="true"
            :rating="application.rating"
            @rating-selected="setCurrentSelectedRating($event, application['pipeline_id'])"
            :id="application['pipeline_id']"
            :border-width="2"
            :show-rating="false"
            :rounded-corners="true"
            :star-points="starPoints"
            :star-size="20"
          ></star-rating>
          <span
            class="reset-stars"
            @click="setCurrentSelectedRating((rating = 0), application['pipeline_id'])"
          >
            <i class="fas fa-times-circle"></i>
          </span>
        </span>
      </div>
    </td>
    <td id="job-pipeline-name" nowrap>
      <router-link
        :to="{ name: 'candidate_id', params: { candidate_id: application.candidate_id } }"
        >{{ application.candidate_first_name }} {{ application.candidate_last_name }}</router-link
      >
    </td>
    <td id="job-pipeline-position" nowrap>
      <router-link :to="{ name: 'job_uuid', params: { job_uuid: application.job_id } }">
        {{ application.job }}</router-link
      >
    </td>
    <td id="job-pipeline-employer" nowrap>
      <router-link
        :to="{ name: 'employer_id', params: { employer_id: application['employer-id'] } }"
      >
        {{ application.employer }}</router-link
      >
    </td>
    <td id="job-pipeline-location" nowrap>
      <span>
        {{ application.hasOwnProperty('location') ? application.location.city : '' }},
        {{ application.hasOwnProperty('location') ? application.location.country : '' }}
      </span>
    </td>
    <td id="service-level" class="p-0" nowrap>
      <div class="popover-show mx-auto" style="width: 25px; height: auto">
        <svgHelper
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          :title="serviceLevel(application.service_level).title"
          :name="serviceLevel(application.service_level).title"
        />
      </div>
    </td>
    <td id="job-pipeline-documents" align="center">
      <div class="d-flex">
        <!--   <div v-for="doc in pipelineDocuments" :key="doc.id"> -->
        <div v-for="doc in application.documents" :key="doc.id">
          <popper
            trigger="hover"
            :options="{
              placement: 'top',
              modifiers: { offset: { offset: '0,10px' } },
            }"
          >
            <div class="popper">{{ doc.name }}</div>
            <a slot="reference">
              <div v-if="getDocumentProperties(doc.name).type === 'pdf'">
                <i
                  @click="openDocPreview(doc.id, doc.name)"
                  :class="[`mr-3 fa`, doc.icon]"
                  :style="
                    `cursor: pointer; color: ${
                      getDocumentProperties(doc.name).color
                    }; font-size: 22px`
                  "
                ></i>
              </div>
              <div v-else>
                <i
                  @click="onDownloadDocument(doc.id, doc.name)"
                  :class="[`mr-3 fa`, doc.icon]"
                  :style="
                    `cursor: pointer; color: ${
                      getDocumentProperties(doc.name).color
                    }; font-size: 22px`
                  "
                ></i>
              </div>
            </a>
          </popper>
        </div>
      </div>
    </td>
    <td id="job-pipeline-applied" nowrap>
      <popper
        trigger="hover"
        :options="{
          placement: 'top',
          modifiers: { offset: { offset: '-60,10px' } },
        }"
      >
        <div class="popper">
          {{
            application.added
              | formatDate({
                format: getUserProfile.format_date,
                timezone: getUserProfile.timezone,
                hasHourAndMinutes: true,
              })
          }}
        </div>
        <div slot="reference">
          {{
            application.added
              | formatDate({
                format: getUserProfile.format_date,
                timezone: getUserProfile.timezone,
                hasHourAndMinutes: false,
              })
          }}
        </div>
      </popper>
    </td>

    <td id="application-table-item-qualification">
      <div class="d-flex align-items-center">
        <div
          :class="[
            getQualificationColorClassByTitle(
              getQualificationMixin(application.qualification).title,
            ),
            'stage-size',
            'rounded-circle',
            'mr-2',
            'flex-shrink-0',
          ]"
        ></div>
        <span class="stage-text text-nowrap">{{
          getQualificationMixin(application.qualification).title
        }}</span>
      </div>
    </td>

    <td id="stage">
      <div class="d-flex align-items-center">
        <div
          :class="[getStageColorClass(application.stage), 'stage-size', 'rounded-circle', 'mr-2']"
        ></div>
        <span class="stage-text text-nowrap">{{ getStageMixin(application.stage).title }}</span>
      </div>
    </td>

    <td id="actions">
      <div class="icon-wrapper d-flex align-items-center">
        <button
          @click="onEditModal"
          :data-bs-target="`#editApplicationModal-${application.pipeline_id}`"
          data-bs-toggle="modal"
          class="btn btn-link"
        >
          <svgHelper class="edit" name="edit" />
        </button>
        <button
          type="button"
          :data-bs-target="`#submitApplicationModal-${application.candidate_id}`"
          data-bs-toggle="modal"
          class="btn btn-link"
          :disabled="getQualificationMixin(application.qualification).value === 'submitted'"
        >
          <svgHelper class="submit" name="submitPipeline" />
        </button>
        <button
          class="btn btn-link"
          data-bs-toggle="modal"
          :data-bs-target="`#candidatePipelineDeleteModal-${application.candidate_id}`"
        >
          <svgHelper class="delete" name="delete" />
        </button>
      </div>
    </td>

    <!-- Edit Modal -->
    <div
      class="modal fade edit-modal"
      :id="`editApplicationModal-${application.pipeline_id}`"
      tabindex="-1"
      aria-labelledby="editApplicationModal"
      aria-hidden="true"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editCandidatePipelineLabel">
              Edit Pipeline - {{ application.candidate_first_name }}
              {{ application.candidate_last_name }}
            </h5>
            <button
              ref="closeEditModal"
              type="button"
              @click="clearForm"
              class="close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group m-label-left">
              <input
                type="text"
                class="form-control mb-3"
                readonly
                disabled
                :value="
                  `${application.job}, ${
                    application.hasOwnProperty('location') ? application.location.city : ''
                  }`
                "
              />
              <label for="stage">Position</label>
            </div>

            <div class="form-group m-label-left">
              <input
                type="text"
                class="form-control mb-3"
                readonly
                disabled
                :value="`${application.employer}`"
              />
              <label for="stage">Employer</label>
            </div>

            <div
              v-if="
                getQualificationMixin(application.qualification).value === 'submitted' ||
                  application.qualification === 'Submitted'
              "
              class="form-group m-label-left"
            >
              <select
                id="stage"
                v-model="selectedStage"
                @change="handleSelectedStage"
                class="form-control"
              >
                <option value="not_submitted">Not submitted</option>
                <option value="not_contacted">Not contacted</option>
                <option value="contacted">Contacted</option>
                <option value="interviewing">Interviewing</option>
                <option value="offered_employment">Offered employment</option>
                <option value="employed">Employed</option>
                <option value="no_longer_interested">No longer interested</option>
                <option value="not_in_consideration">Not in consideration</option>
              </select>
              <label for="stage">Stage</label>
            </div>

            <div class="form-group m-label-left mb-4" v-else>
              <select
                id="select-item-qualification"
                class="form-control"
                v-model="selectedQualification"
                @change="handleSelectedQualification"
              >
                <option value="not_contacted">Not contacted</option>
                <option value="qualifying">Qualifying</option>
                <option value="contacted">Contacted</option>
                <option value="responded">Responded</option>
                <option value="interviewing">Interviewing</option>
                <option value="no_longer_interested">No longer interested</option>
                <option value="not_in_consideration">Not in consideration</option>
              </select>
              <label for="qualification">Qualification</label>
            </div>

            <div class="form-group m-label-left mb-4">
              <select v-model="selectedSource" class="form-control" id="application-source">
                <option v-for="source in getSourceList" :key="source.id" :value="source">
                  {{ source.description }} ({{ source.source_name }})
                </option>
              </select>
              <label for="source">Source</label>
            </div>

            <div class="form-group">
              <ul class="list-group file-listing mt-3">
                <li
                  :key="file.id"
                  v-for="file in candidateDocumnets"
                  class="list-group-item d-flex align-items-center small"
                >
                  <div class="custom-control custom-checkbox d-flex">
                    <input
                      :id="`edit-pipeline-${file.id}`"
                      class="custom-control-input"
                      type="checkbox"
                      v-model="selectedDocuments"
                      @change="handleCheckboxChange"
                      :value="file"
                      :disabled="isLoading"
                      aria-label
                    />
                    <label class="custom-control-label" :for="`edit-pipeline-${file.id}`">
                      {{ file.name }}
                    </label>
                  </div>

                  <span class="badge badge-primary badge-pill ml-auto">{{
                    `${(file.file_size ? file.file_size / 1024 / 1024 : 0).toFixed(2)} MB`
                  }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="modal-footer text-center justify-content-center">
            <button @click="onUpdatePipelineRow(application)" type="button" class="btn btn-primary">
              Save changes
            </button>
            <button type="button" @click="onCancelPipeline" class="btn btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- End Edit Modal -->

    <!-- Submit Modal -->
    <div
      class="modal fade"
      :id="`submitApplicationModal-${application.candidate_id}`"
      tabindex="-1"
      aria-labelledby="submitApplicationModal"
      aria-hidden="true"
      data-backdrop="static"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="submitApplicationModalLabel">Submit Application</h5>
            <button
              ref="closeSubmitModal"
              type="button"
              class="close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="d-flex align-items-center">
              <p>
                Please confirm that
                <b>{{ application.candidate_first_name }} {{ application.candidate_last_name }}</b>
                should be submitted for the role
                <b>{{ application.job }}</b
                >,
                {{ application.employer || 'N/A' }}
              </p>
            </div>
            <textarea
              :disabled="isSending"
              v-model="pipelineComment"
              class="form-control"
              rows="2"
              placeholder="Message to Employer"
            ></textarea>
          </div>

          <div class="modal-footer text-center justify-content-center">
            <button
              :disabled="isSending"
              @click="onSubmitPipeline(application)"
              type="button"
              class="btn btn-primary"
            >
              Submit
            </button>
            <button
              :disabled="isSending"
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- End Submit Modal -->

    <!-- Delete Modal -->
    <div
      class="modal fade"
      :id="`candidatePipelineDeleteModal-${application.candidate_id}`"
      tabindex="-1"
      aria-labelledby="candidatePipelineDeleteModal"
      aria-hidden="true"
      data-backdrop="static"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editModal2Label">Remove From Applications</h5>
            <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body text-center justify-content-center">
            <p>
              Please confirm that you want to remove
              <b>{{ application.candidate_first_name }} {{ application.candidate_last_name }}</b>
              from this pipeline.
            </p>
          </div>
          <div class="modal-footer text-center justify-content-center">
            <button
              @click="onDeletePipeline"
              type="button"
              class="btn btn-primary"
              data-bs-dismiss="modal"
            >
              Delete
            </button>

            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          </div>
        </div>
      </div>
    </div>
    <!-- End Delete Modal -->
  </tr>
</template>

<script>
import { stagesMixin } from '../../../mixins/stagesMixin';
import { qualificationMixin } from '../../../mixins/qualificationMixin';
import { mapActions, mapGetters } from 'vuex';
import { serviceLevelMixin } from '../../../mixins/serviceLevelMixin';
import { errorService } from '../../../services/errorService';
// import moment from 'moment';

export default {
  mixins: [stagesMixin, qualificationMixin, serviceLevelMixin],
  props: {
    application: {
      type: Object,
    },
    user: {
      type: Object,
    },
  },
  data: () => ({
    isSending: false,
    pipelineComment: '',
    isLoading: false,
    selectedSource: {},
    selectedStage: '',
    selectedQualification: '',
    candidateNames: '',
    candidateDocumentsFiles: [],
    starPoints: [23, 2, 14, 17, 0, 19, 10, 34, 7, 50, 23, 43, 38, 50, 36, 34, 46, 19, 31, 17],
    selectedDocuments: [],
    pipelineDocuments: [],
    candidateDocumnets: [],
  }),
  methods: {
    ...mapActions([
      'setCandidatePipelineRating',
      'retrieveDocumentPreview',
      'retrieveSourcesList',
      'retrieveReviewApplications',
      'updatePipelineForCandidate',
      'removeReviewApplication',
      'submitCandidatePipeline',
      'retrieveCandidateById',
      'retrieveCandidateDocument',
    ]),
    setCurrentPipeline() {
      if (this.currentQualification !== 'Submitted') {
        this.currentQualification = this.getQualificationMixin(this.selectedQualification).title;
      }

      if (this.currentQualification === 'Submitted') {
        this.currentStage = this.getStageMixin(this.selectedStage).title;

        /* Set Modal Values */
        this.selectedStage = this.getStageMixin(this.selectedStage).title;
      }

      this.currentSource = this.selectedSource;
      this.currentCreatedAt = this.application.created_at;
      this.pipelineDocuments = [...this.selectedDocuments];
    },
    onEditModal() {
      //  this.selectedDocuments = this.pipelineDocuments;
      // Set values in modals from db
      this.retrieveSourcesList().then(() => {
        this.selectedSource = {
          ...this.application.source,
        };
      });

      this.selectedQualification = this.getQualificationMixin(this.application.qualification).value;
      this.selectedStage = this.getStageMixin(this.application.stage).value;

      if (this.application.candidate_id) {
        this.retrieveCandidateById({ uuid: this.application.candidate_id }).then(({ data }) => {
          this.candidateDocumnets = data.documents;

          this.selectedDocuments = this.application.documents;
        });
      }
    },
    handleCheckboxChange() {},
    clearForm() {
      this.onCancelPipeline();
    },
    getDocumentProperties(fileName) {
      const re = /(?:\.([^.]+))?$/;
      if (re.exec(fileName)[1] === 'pdf') {
        return {
          color: '#ff0000',
          type: 'pdf',
        };
      } else if (re.exec(fileName)[1] === 'docx' || re.exec(fileName)[1] === 'doc') {
        return {
          color: '#005a9e',
          type: 'doc',
        };
      } else {
        return { color: '#a7a7a77', type: '' };
      }
    },
    setCurrentSelectedRating(rating, pipelineId) {
      this.setCandidatePipelineRating({ rating, pipeline_id: pipelineId })
        .then(() => {
          this.retrieveCandidatePipelines({ uuid: this.$route.params.candidate_id });
        })
        .catch(err => {
          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 5000,
          });
        });
    },
    openDocPreview(docId, name) {
      const fileExtension = name
        .split('.')
        .pop()
        .toLowerCase();
      const imageFileTypes = ['jpg', 'jpeg', 'png', 'heic'];
      if (imageFileTypes.includes(fileExtension)) {
        this.retrieveDocumentPreview({ uuid: docId, preview: 'true' })
          .then(response => {
            let mimeType;
            switch (fileExtension) {
              case 'png':
                mimeType = 'image/png';
                break;
              case 'heic':
                mimeType = 'image/heic';
                break;
              default:
                mimeType = 'image/jpeg';
            }

            const blob = new window.Blob([response.data], { type: mimeType });
            const fileURL = window.URL.createObjectURL(blob);

            // Open the image in a new tab
            const newWindow = window.open('', '_blank');
            newWindow.document.write(`
          <html>
            <head>
              <title>Image Preview</title>
            </head>
            <body style="margin:0;">
              <img src="${fileURL}" style="width:100%; height:100%; object-fit:contain;" />
            </body>
          </html>
        `);
            newWindow.document.close();
          })
          .catch(() => {
            this.isCvLoading = false;
          });
      } else {
        this.isCvLoading = true;
        this.retrieveDocumentPreview({ uuid: docId, preview: 'true' })
          .then(response => {
            this.isCvLoading = false;
            const blob = new window.Blob([response.data], { type: 'application/pdf' });

            const fileURL = window.URL.createObjectURL(blob);

            window.open(fileURL, '_blank').focus();
          })
          .catch(() => {
            this.isCvLoading = false;
          });
      }
    },
    onDownloadDocument(docId, docTitle) {
      this.retrieveCandidateDocument({ uuid: docId })
        .then(response => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement('a');

          fileLink.href = fileURL;
          fileLink.setAttribute('download', `${docTitle}`);
          document.body.appendChild(fileLink);

          fileLink.click();
        })
        .catch(err => {
          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 8000,
          });
        });
    },
    handleSelectedQualification() {
      if (this.selectedQualification === 'submitted') {
        this.selectedStage = 'not_contacted';
      }
    },
    handleSelectedStage() {},
    onUpdatePipelineRow(activityRow) {
      this.isLoading = true;

      this.updatePipelineForCandidate({
        pipeline_id: activityRow.pipeline_id,
        stage: this.selectedStage,
        qualification: this.selectedQualification,
        source_id: this.selectedSource.id,
        documents: this.selectedDocuments,
        programming_test: -1,
      })
        .then(({ data }) => {
          this.isLoading = false;

          this.$emit('update-activity-row', data);

          this.$toasted.success('Pipeline updated', {
            theme: 'bubble',
            icon: 'fa-check-circle',
            position: 'bottom-right',
            duration: 5000,
          });
        })
        .then(() => {
          this.$refs.closeEditModal.click();
        })
        .catch(err => {
          this.isLoading = false;

          this.$toasted.error(errorService(err), {
            theme: 'bubble',
            icon: 'fa-times-circle',
            position: 'bottom-right',
            duration: 5000,
          });
        });
    },
    onCancelPipeline() {
      /* this.selectedSource = this.application.source;
      this.selectedQualification = this.getQualificationMixin(this.application.qualification).value;
      this.selectedStage = this.getStageMixin(this.application.stage).value;
      this.selectedSource = this.application.source; */
      this.$refs.closeEditModal.click();
    },
    onSubmitPipeline(applicationRow) {
      this.isSending = true;
      this.submitCandidatePipeline({
        pipeline_id: this.application.pipeline_id,
        stage: this.getStageMixin(this.application.stage).value,
        qualification: 'submitted',
        source_id: this.selectedSource.id,
        documents: this.application.documents,
        submitted_comment: this.pipelineComment,
      })
        .then(() => {
          this.selectedQualification = 'Submitted';
          this.selectedStage = 'Not contacted';
          /* this.currentQualification = 'Submitted';
          this.selectedStage = 'Not contacted';
          this.currentCreatedAt = moment().format(); */

          this.$toasted.show('Pipeline submitted', {
            theme: 'bubble',
            icon: 'fa-check-circle',
            position: 'bottom-right',
            duration: 5000,
          });

          //this.$emit('application-reload');
          this.$emit('submit-activity-row', { ...applicationRow, qualification: 4, stage: 102 });
        })
        .then(() => {
          this.isSending = false;
          this.$refs.closeSubmitModal.click();
        });
    },
    onDeletePipeline() {
      this.removeReviewApplication(this.application.pipeline_id).then(() => {
        this.$emit('application-reload');
      });
    },
  },
  watch: {
    application: {
      handler(newVal, oldVal) {
        // Kick's when user add candidate from AddCandidateBtn
        if (oldVal !== '') {
          /*  this.currentQualification = this.getQualificationMixin(newVal.qualification).title;
          this.currentSource = newVal.source;
          this.currentCreatedAt = newVal.created_at; */
          /* SET MODAL VALUES */
          /*   this.selectedQualification = this.getQualificationMixin(newVal.qualification).value;
          this.selectedSource = newVal.source;
          this.selectedStage = this.getStageMixin(newVal.stage).value; */
        }
        //  this.currentStage = this.getStageMixin(this.application.stage).title;
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    ...mapGetters(['getSourceList', 'getUserProfile']),
  },
  mounted() {
    this.pipelineDocuments = [...this.application.documents];

    /*  this.pipelineDocuments = [...this.application.documents];
    this.currentCreatedAt = this.application.updated_at; */
    /*   console.log(this.getStageMixin(this.application.stage).title);
    console.log(this.getQualificationMixin(this.application.qualification).title); */
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';
@import '@/assets/scss/_mixins';

.star-rating {
  position: relative;
  display: block;
  width: 100%;
  .reset-stars {
    position: absolute;
    font-size: 16.7px;
    cursor: pointer;
    top: 0px;
    right: -8px;
    color: #555;
    display: none;
  }
  &:hover {
    .reset-stars {
      display: block;
    }
  }
}

.icon-wrapper {
  .svg-icon {
    width: 18px;
    height: 18px;
  }

  .edit {
    fill: #a7a7a7;
  }

  .submit {
    fill: #5bb75b;
  }
}
</style>
